import React, { FC, memo, useEffect, useState } from 'react';

import { ArrowDownToSquare } from '@gravity-ui/icons';
import { Button, Icon, Progress, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/ApkFormFields';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as settingsKeyset from 'i18n/Settings';

import styles from './ApkLink.module.scss';

interface Props {
    url: string;
    fileName: string;
}

const t = i18n(keyset);
const tSettings = i18n(settingsKeyset);
const tGeneralSettings = i18n(generalSettingsKeyset);

function downloadBlob(blobData: BlobPart, fileName: string) {
    const blob = new Blob([blobData], { type: 'application/vnd.android.package-archive' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, 100);
}

const ApkLinkComponent: FC<Props> = ({ url, fileName }) => {
    const [loadingUrl, setLoadingUrl] = useState('');
    const [progress, setProgress] = useState(0);

    const { add: addToast } = useToaster();

    useEffect(() => {
        const controller = new AbortController();

        const downloadWithProgress = async () => {
            setProgress(0);
            try {
                const response = await fetch(url, { signal: controller.signal });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const contentLength = response.headers.get('Content-Length') || '0';
                const total = parseInt(contentLength, 10);
                let loaded = 0;

                if (!response.body) {
                    throw new Error('No response.body');
                }

                const reader = response.body.getReader();
                const chunks = [];

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) break;

                    chunks.push(value);
                    loaded += value.length;

                    const currentProgress = Math.trunc(total ? (loaded / total) * 100 : 0);
                    if (currentProgress !== progress) setProgress(currentProgress);
                }

                const blob = new Blob(chunks);
                downloadBlob(blob, fileName);
            } catch (error) {
                if (controller.signal.aborted) return;
                console.error('@ApkLink Download failed:', error);
                addToast({ title: tGeneralSettings('error'), theme: 'danger', name: 'APK_LOADING_ERROR' });
            } finally {
                if (!controller.signal.aborted) {
                    setProgress(0);
                    setLoadingUrl('');
                }
            }
        };
        if (loadingUrl) {
            downloadWithProgress();
        }

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingUrl, addToast, url, fileName]);

    return (
        <div className={styles.apkLink__downloadButtonContainer}>
            <Button onClick={() => setLoadingUrl(url)} loading={!!loadingUrl}>
                <Icon data={ArrowDownToSquare} />
                {t('download')}
            </Button>
            {progress ? (
                <Progress className={styles.apkLink__progress} value={progress} text={tSettings('icon-is-loading')} />
            ) : null}
        </div>
    );
};

export const ApkLink = memo(ApkLinkComponent);
ApkLink.displayName = 'ApkLink';
